/**
 * PurchasingTab — Dashboard tab showing all pending parts across jobs.
 *
 * Groups by vendor (from quote) → part number → jobs needing that part.
 * Parts without quotes show under "Unassigned".
 * Jobs sorted by priority (high first), then oldest.
 *
 * Includes bulk PO upload for ordering across multiple jobs.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPendingParts } from '../../api/purchasing';
import { fetchEmailMessage, fetchEmailAttachments, openAttachment } from '../../api/rfq';
import { LoadingSpinner, EmptyState, Button } from '../common';
import BulkPoUploadModal from './BulkPoUploadModal';

const PRIORITY_BADGE = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

export default function PurchasingTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedVendors, setExpandedVendors] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);

  // Email viewer
  const [viewEmail, setViewEmail] = useState(null); // { subject, body, from, date, attachments }
  const [emailLoading, setEmailLoading] = useState(false);

  async function handleViewEmail(emailId) {
    if (!emailId) return;
    setEmailLoading(true);
    try {
      const email = await fetchEmailMessage(emailId);
      let attachments = [];
      try { attachments = await fetchEmailAttachments(emailId); } catch {}
      setViewEmail({ ...email, _emailId: emailId, _attachments: attachments });
    } catch {
      alert('Could not load email.');
    } finally {
      setEmailLoading(false);
    }
  }

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const result = await fetchPendingParts();
      setData(result);
      // Expand all vendors by default
      const expanded = {};
      (result.vendors || []).forEach(v => { expanded[v.vendor_id || 'unassigned'] = true; });
      setExpandedVendors(expanded);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load pending parts');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  function toggleVendor(vendorId) {
    const key = vendorId || 'unassigned';
    setExpandedVendors(prev => ({ ...prev, [key]: !prev[key] }));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
        {error}
        <button onClick={loadData} className="ml-3 underline">Retry</button>
      </div>
    );
  }

  if (!data?.vendors?.length) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-700">Purchasing</h2>
          <Button size="sm" onClick={() => setUploadOpen(true)} disabled>Upload PO</Button>
        </div>
        <EmptyState
          title="No parts need ordering"
          description="All BOM lines across active jobs have been ordered or received."
        />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-700">
            Purchasing
            <span className="text-sm font-normal text-gray-400 ml-2">
              ({data.total_parts} parts • {data.total_jobs} jobs)
            </span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload PO
            </span>
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-violet-50 border border-violet-200 rounded-md p-3 mb-4 flex items-start gap-2">
        <span className="text-violet-500 mt-0.5">ℹ️</span>
        <p className="text-xs text-violet-700">
          <strong>Bulk Ordering:</strong> Upload a PO to automatically allocate parts across multiple jobs. 
          Parts are allocated by priority (high → low), then oldest job first. Excess quantities are treated as stock.
        </p>
      </div>

      {/* Vendor groups */}
      <div className="space-y-3">
        {data.vendors.map(vendor => {
          const key = vendor.vendor_id || 'unassigned';
          const expanded = expandedVendors[key] !== false;
          const isUnassigned = !vendor.vendor_id;

          return (
            <div
              key={key}
              className={`border rounded-lg overflow-hidden ${isUnassigned ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 bg-white'}`}
            >
              {/* Vendor header */}
              <button
                onClick={() => toggleVendor(vendor.vendor_id)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors ${isUnassigned ? 'hover:bg-amber-100/50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <svg
                    className={`w-4 h-4 text-gray-400 transform transition-transform ${expanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <div>
                    <span className={`font-semibold ${isUnassigned ? 'text-amber-700' : 'text-gray-900'}`}>
                      {isUnassigned ? '⚠ Unassigned' : vendor.vendor_name}
                    </span>
                    {vendor.vendor_code && !isUnassigned && (
                      <span className="text-xs text-gray-400 ml-2">({vendor.vendor_code})</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  {vendor.needs_response_count > 0 && (
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                      ⚠️ {vendor.needs_response_count} needs response
                    </span>
                  )}
                  <span>{vendor.parts_count} part{vendor.parts_count !== 1 ? 's' : ''}</span>
                  <span>{vendor.jobs_count} job{vendor.jobs_count !== 1 ? 's' : ''}</span>
                </div>
              </button>

              {/* Parts list */}
              {expanded && (
                <div className="border-t border-gray-100 divide-y divide-gray-100">
                  {vendor.parts.map(part => (
                    <div key={part.part_id} className="px-4 py-3">
                      {/* Part header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-sm font-semibold text-gray-800">{part.part_number}</span>
                          {part.vendor_part_number && part.vendor_part_number !== part.part_number && (
                            <>
                              <span className="text-xs text-gray-400">VPN:</span>
                              <span className="font-mono text-xs text-gray-600">{part.vendor_part_number}</span>
                            </>
                          )}
                          <span className="text-xs text-gray-400">—</span>
                          <span className="text-xs text-gray-600">{part.description}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs font-medium text-gray-700 shrink-0 ml-3">
                          {part.quote_email_id && (
                            <button
                              onClick={() => handleViewEmail(part.quote_email_id)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                              title="View vendor quote email"
                              disabled={emailLoading}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              View Email
                            </button>
                          )}
                          {part.quoted_price != null && (
                            <span className="text-green-700">${Number(part.quoted_price).toFixed(2)}/ea</span>
                          )}
                          <span>Total: <span className="text-blue-600">{part.total_qty_needed}</span></span>
                        </div>
                      </div>

                      {/* Jobs needing this part */}
                      <div className="ml-4 space-y-1">
                        {part.jobs.map(job => (
                          <div key={job.bom_line_id} className="flex items-center gap-3 text-xs">
                            <span className="text-gray-400">→</span>
                            <button
                              onClick={() => navigate(`/jobs/${job.job_id}`)}
                              className="font-medium text-blue-600 hover:underline"
                            >
                              Job #{job.job_number}
                            </button>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PRIORITY_BADGE[job.priority] || PRIORITY_BADGE.medium}`}>
                              {job.priority}
                            </span>
                            <span className="text-gray-500">
                              needs <span className="font-medium text-gray-700">{job.qty_needed}</span>
                            </span>
                            {job.customer_name && (
                              <span className="text-gray-400">
                                ({job.customer_name})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <BulkPoUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={loadData}
      />

      {/* Email viewer modal */}
      {viewEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setViewEmail(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">{viewEmail.subject || '(no subject)'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">From: {viewEmail.sender || viewEmail.from || '—'}</p>
              </div>
              <button onClick={() => setViewEmail(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none ml-3">×</button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{viewEmail.body_text || viewEmail.body || '(empty body)'}</pre>
            </div>
            {viewEmail._attachments?.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500">Attachments:</span>
                {viewEmail._attachments.map((att, i) => (
                  <button
                    key={i}
                    onClick={() => openAttachment(viewEmail._emailId, att.id, att.filename)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100"
                  >
                    📎 {att.filename}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
